import { z } from 'zod'

/**
 * A subsidy rate, in basis points. One basis point is 1/100 of a percent, so
 * 80% is 8000 and the full range is 0..10000.
 *
 * ## Why a validated type rather than a number
 *
 * The calculation refuses a rate finer than a basis point rather than rounding
 * it silently. Left as a raw `number`, that refusal happens when a bill is
 * processed — so a rule authored with an unrepresentable rate passes review,
 * passes typecheck, and fails on a live bill months later, for one scheme, on
 * one service date. Validating at the point the rule is written moves that
 * failure to where someone can still do something about it.
 */
declare const basisPointsBrand: unique symbol

const rateSchema = z
  .number()
  .int('a rate must be a whole number of basis points')
  .min(0, 'a rate must not be negative')
  .max(10_000, 'a rate must not exceed 10000 basis points (100%)')

export const BasisPoints = rateSchema.brand<typeof basisPointsBrand>()
export type BasisPoints = z.infer<typeof BasisPoints>

/** Parse-or-throw, for rule authoring and for the edges where a raw number arrives. */
export const basisPoints = (value: number): BasisPoints => BasisPoints.parse(value)

/**
 * Convert a percentage to basis points, refusing anything not exactly
 * representable rather than rounding it into a subsidy calculation.
 */
export function basisPointsFromPercentage(percentage: number): BasisPoints {
  if (!Number.isFinite(percentage)) {
    throw new RangeError(`percentage must be finite, received ${percentage}`)
  }
  const scaled = percentage * 100
  const rounded = Math.round(scaled)
  if (Math.abs(scaled - rounded) > 1e-9) {
    throw new RangeError(
      `percentage ${percentage} is not expressible in whole basis points; ` +
        `a rate we cannot represent exactly is a question for the Authority, not ` +
        `something to approximate inside a subsidy calculation`,
    )
  }
  return basisPoints(rounded)
}
